<template>
  <main-layout v-if="wheel">
    <template slot="header-title">{{ wheel.name }}</template>
    <template slot="header-actions">
      <common-button type="secondary">
        <router-link class="text-gray-600" :to="{ name: 'Wheels', params: { site: site.sitekey } }">{{
          $t('wheel.actions.cancel')
        }}</router-link>
      </common-button>
      <common-button @click="saveClick()">{{ $t('wheel.actions.save') }}</common-button>
    </template>

    <template slot="main">
      <common-ui-form-general
        v-if="wheel"
        type="wheel"
        :code="wheel.code"
        :name="wheel.name"
        :status="wheel.status"
        :activationdate="wheel.activationdate"
        :deactivationdate="wheel.deactivationdate"
        :loading="loading"
        @update-name="name => (fields.name = name)"
        @update-status="status => (fields.status = status)"
        @update-activationdate="dateStart => updateDateStart(dateStart)"
        @update-deactivationdate="dateEnd => updateDateEnd(dateEnd)"
      />

      <common-ui-form-targeting
        v-if="wheel"
        type="wheel"
        :current-url="wheel.url"
        :target-url="wheel.url_target"
        :target-url-list="wheel.url_target_list"
        :exclude-url="wheel.url_exclude_list"
        @update-fields-url="url => (fields.url = url)"
        @update-path-target="urlTarget => (fields.url_target_list = urlTarget)"
        @update-path-exclude="urlExclude => (fields.url_exclude_list = urlExclude)"
      />

      <common-ui-form-audience
        v-if="wheel && wheel.target_audience && wheel.bubbles"
        :key="audienceKey"
        type="wheel"
        :target-audience="wheel.target_audience"
        :excluded-audience="wheel.excluded_audience"
        :bubbles="wheel.bubbles"
        :excluded-bubbles="wheel.excluded_bubbles"
        @update-audience="audienceData => (fields.target_audience = audienceData)"
        @update-excluded-audience="audienceData => (fields.excluded_audience = audienceData)"
        @update-bubbles="bubble => updateFieldsBubble(bubble)"
        @update-excluded-bubbles="bubble => updateFieldsExcludedBubble(bubble)"
        @erase-bubble="bubbles => (fields.bubbles = bubbles)"
        @erase-excluded-bubble="bubbles => (fields.excluded_bubbles = bubbles)"
      />

      <wheels-ui-form-gifts
        v-if="wheel"
        type="wheel"
        :wheelId="wheel.id"
        :name="wheel.name"
        :giftEnable="wheel.giftenabled"
        @update-name="name => (fields.name = name)"
        @update-giftEnable="giftEnable => updateGiftEnable(giftEnable)"
        @edited-gift="save()"
      />

      <wheels-ui-form-points
        v-if="wheel"
        type="wheel"
        :prices="wheel.prices"
        @update-PointsText="newLink => (fields.PointsText = newLink)"
      />

      <template>
        <div style="position: fixed; bottom: 20px; left: 20px; margin: 0; z-index: 9999;">
          <common-ui-alert
            type="warning"
            message="You have modification not saved. Don't forget to save it."
            :base="wheel"
            :object="fields"
          />
        </div>
      </template>
    </template>
  </main-layout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import VueDropdown from '@kevindesousa/vue-dropdown'
import Empty from '@/components/box-entity-empty.vue'
import ModalConfirmation from '@/components/others/utils/ModalConfirmation.vue'
import ModalMultipleChoices from '@/components/others/utils/ModalMultipleChoices.vue'

export default {
  name: 'Wheel',
  components: {
    VueDropdown,
    Empty,
    ModalConfirmation,
    ModalMultipleChoices
  },

  props: {
    id: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      isClosed: false,
      loaded: false,
      fieldsError: {
        name: null,
        url: null,
        date: null
      },
      fields: {
        activationdate: null,
        deactivationdate: null,
        prices: [],
        id: null,
        name: null,
        code: null,
        status: null,
        giftenabled: 0,
        url: null,
        datecreation: null,
        url_target: null,
        url_target_list: [],
        url_exclude_list: [],
        target_audience: null,
        excluded_audience: null,
        bubbles: [],
        excluded_bubbles: [],
        views: [],
        lastrun: []
      },
      inputs: {
        redirectionUrl: null,
        targetAll: false,
        event: false
      },
      extra: {
        campaigns: null
      },
      loading: false,
      PointsText: [],
      audienceKey: 4321
    }
  },

  computed: {
    ...mapGetters('account', {
      site: 'currentSite',
      lang: 'defaultLang'
    }),

    ...mapGetters('wheel', {
      getById: 'getWheelById'
    }),

    languages() {
      return this.site.languages.map(lang => lang.lang)
    },

    wheel() {
      return this.getById(this.id)
    }
  },

  watch: {
    site: {
      handler: 'initWheel',
      immediate: true
    }
  },

  methods: {
    ...mapActions('wheel', ['loadWheelById', 'updateWheel', 'removeWheel']),
    ...mapActions('campaigns', ['loadCampaign']),

    initWheel() {
      if (this.site) {
        // 1. Load the wheel
        this.loadWheelById({ id: this.id })
          .catch(() => {
            this.$notify.error({
              title: 'Error',
              message: 'An error has occurred'
            })
          })
          .then(wheel => {
            this.fields = Object.assign({}, wheel)
          })
      }
    },

    updateFieldsBubble(bubble) {
      const indexBubble = this.fields.bubbles.findIndex(b => b.id === bubble.id)
      const bubbleExist = indexBubble !== -1
      if (bubbleExist) {
        this.fields.bubbles.splice(indexBubble, 1)
      }

      this.fields.bubbles.push(bubble)
    },

    updateFieldsExcludedBubble(bubble) {
      const indexBubble = this.fields.excluded_bubbles.findIndex(b => b.id === bubble.id)
      const bubbleExist = indexBubble !== -1
      if (bubbleExist) {
        this.fields.excluded_bubbles.splice(indexBubble, 1)
      }

      this.fields.excluded_bubbles.push(bubble)
    },

    checkInputs() {
      const ValidHostnameRegex = /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$/

      let error = false

      if (!this.fields.name) {
        this.fieldsError.name = 'Name must be a valid string, like: Welcome dear !'
        error = true
      }

      if (
        !this.fields.url ||
        !Array.isArray(this.fields.url.match(ValidHostnameRegex)) ||
        (Array.isArray(this.fields.url.match(ValidHostnameRegex)) && !this.fields.url.match(ValidHostnameRegex).length)
      ) {
        this.fieldsError.url = 'Hostname must be a valid string, like: rocketfid.com'
        error = true
      }

      return !error
    },
    updateStep(step) {
      this.fields.steps[step.index] = step
    },
    async saveClick() {
      await this.save().then(() => {
        this.$notify({
          title: 'Success',
          message: 'Wheel has been updated',
          type: 'success'
        })
      }).catch(() => {
        this.$notify({
          title: 'Error',
          message: 'An error occured',
          type: 'error'
        })
      })
    },
    async save() {
      this.fieldsError.name = null
      this.fieldsError.url = null
      this.fieldsError.date = null

      if (this.fields.target_audience === 'all') {
        this.fields.bubbles = []
      }

      if (this.fields.excluded_audience === 'no-one') {
        this.fields.excluded_bubbles = []
      }

      if (!this.checkInputs()) {
        return false
      }

      let newWheel = this.fields
      try {
        newWheel = await this.updateWheel(this.fields)
      } catch (error) {
        console.error(error)
        this.$notify({
          title: 'Error',
          message: 'An error occured',
          type: 'error'
        })
      }
      this.fields = newWheel
    },
    updateDateStart(ds) {
      this.fields.activationdate = ds
    },

    updateDateEnd(de) {
      this.fields.deactivationdate = de
    },

    updateGiftEnable(giftEnable) {
      this.fields.giftenabled = giftEnable
      this.save()
    },
    remove() {
      this.removeWheel({ wheel: { id: this.id } })
        .then(() => {
          this.$notify({
            title: 'Success',
            message: this.$root.$t('notifications.wheel.deleted'),
            type: 'success'
          })
        })
        .catch(response => {
          this.$notify.error({
            title: this.$root.$t('api.' + response.data.code + '.title'),
            message: this.$root.$t('api.' + response.data.code + '.message')
          })
        })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/Wheel/Wheel.vue